import { useCallback, useMemo, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { Check, Copy, Download, ImageOff } from 'lucide-react'
import { api } from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { EmptyState } from '@/components/ui/empty-state'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { downloadImageAsFile, getImageTypeLabel } from '@/lib/asset-file-utils'
import { cn } from '@/lib/utils'

interface LogoAsset {
  objectKey: string
  fileName: string
  url: string
  publicUrl?: string | null
}

interface LogoAssetsPage {
  items: LogoAsset[]
  cursor?: string
}

/**
 * Optional: scope listing to a key prefix (e.g. `logos/`). Leave unset to list all image objects.
 */
const LOGO_PREFIX: string | undefined = import.meta.env.VITE_LOGO_ASSETS_PREFIX

/** Prefer direct R2/CDN URL when API sets `publicUrl` (IMAGES_PUBLIC_BASE_URL on the Worker). */
function logoAssetHref(item: LogoAsset) {
  return item.publicUrl ?? item.url
}

interface LogoCardProps {
  item: LogoAsset
  copied: boolean
  onCopy: (item: LogoAsset) => void
  onOpenPreview: (item: LogoAsset) => void
}

function LogoCard({ item, copied, onCopy, onOpenPreview }: LogoCardProps) {
  const { toast } = useToast()
  const [broken, setBroken] = useState(false)
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null)
  const [isDownloading, setIsDownloading] = useState(false)

  const typeLabel = useMemo(() => getImageTypeLabel(item.fileName), [item.fileName])

  const dimensionLabel = (() => {
    if (broken) {
      return '—'
    }
    if (dims == null) {
      return '…'
    }
    if (dims.w > 0 && dims.h > 0) {
      return `${dims.w} × ${dims.h}`
    }
    return '—'
  })()

  const handleCopyClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    onCopy(item)
  }

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    setIsDownloading(true)
    try {
      const downloadUrl = item.publicUrl ?? item.url
      const outcome = await downloadImageAsFile(downloadUrl, item.fileName, {
        credentials: item.publicUrl ? 'omit' : 'include',
      })
      if (outcome === 'fallback') {
        toast({
          title: 'Opened in a new tab',
          description:
            'Automatic download was blocked. Save the image from the new tab (e.g. right-click → Save image as).',
        })
      } else {
        toast({
          title: 'Download started',
          description: item.fileName,
        })
      }
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Card
      className={cn(
        'overflow-hidden border-border transition-colors',
        copied && 'ring-2 ring-mint/60 border-mint/40'
      )}
    >
      <CardContent className="p-0">
        <button
          type="button"
          disabled={broken}
          onClick={() => {
            if (!broken) {
              onOpenPreview(item)
            }
          }}
          className={cn(
            'group relative flex aspect-4/3 w-full cursor-zoom-in items-center justify-center bg-surface2 p-3 outline-hidden transition-colors',
            'hover:bg-surface2/90 focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
            broken && 'cursor-not-allowed opacity-80'
          )}
          aria-label={broken ? 'Preview unavailable' : `View full size: ${item.fileName}`}
        >
          {broken ? (
            <div className="flex flex-col items-center gap-2 text-textMuted">
              <ImageOff className="h-10 w-10" aria-hidden />
              <span className="text-xs">Preview unavailable</span>
            </div>
          ) : (
            <img
              src={logoAssetHref(item)}
              alt=""
              loading="lazy"
              decoding="async"
              className="max-h-full max-w-full object-contain pointer-events-none"
              onLoad={e => {
                const { naturalWidth, naturalHeight } = e.currentTarget
                setDims({ w: naturalWidth, h: naturalHeight })
              }}
              onError={() => {
                setBroken(true)
                setDims(null)
              }}
            />
          )}
        </button>
        <div className="space-y-2 border-t border-border p-3">
          <p className="truncate text-center text-sm font-medium text-text" title={item.fileName}>
            {item.fileName}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1.5">
            <Badge variant="secondary" className="font-semibold tracking-wide">
              {typeLabel}
            </Badge>
            <span
              className="text-xs text-textMuted tabular-nums"
              title={broken || dims == null ? undefined : `${dims.w} by ${dims.h} pixels`}
            >
              {dimensionLabel}
            </span>
          </div>
          <div className="flex items-center justify-center gap-1.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={handleCopyClick}
                  aria-label={copied ? 'URL copied' : `Copy URL for ${item.fileName}`}
                >
                  {copied ? (
                    <Check className="h-3.5 w-3.5 text-mint" aria-hidden />
                  ) : (
                    <Copy className="h-3.5 w-3.5" aria-hidden />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">{copied ? 'Copied' : 'Copy URL'}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  disabled={broken || isDownloading}
                  onClick={handleDownload}
                  aria-label={isDownloading ? 'Downloading' : `Download ${item.fileName}`}
                >
                  <Download className="h-3.5 w-3.5" aria-hidden />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {isDownloading ? 'Downloading…' : 'Download file'}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

interface LogoPreviewDialogProps {
  item: LogoAsset | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

function LogoPreviewDialog({ item, open, onOpenChange }: LogoPreviewDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        key={item?.objectKey ?? 'preview'}
        className={cn(
          'max-h-[92vh] w-full max-w-[min(96vw,72rem)] gap-0 overflow-hidden border-border bg-surface p-0',
          'sm:max-w-[min(96vw,72rem)]'
        )}
      >
        {item ? (
          <>
            <DialogHeader className="space-y-0 border-b border-border px-4 py-3 pr-14 text-left">
              <DialogTitle
                className="truncate text-base font-semibold leading-snug"
                title={item.fileName}
              >
                {item.fileName}
              </DialogTitle>
            </DialogHeader>
            <div className="flex max-h-[min(80vh,820px)] min-h-48 items-center justify-center overflow-auto bg-surface2 p-4 sm:p-6">
              <img
                src={logoAssetHref(item)}
                alt=""
                className="max-h-full max-w-full object-contain"
              />
            </div>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

export function LogosTab() {
  const { toast } = useToast()
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const [previewItem, setPreviewItem] = useState<LogoAsset | null>(null)

  const prefix = LOGO_PREFIX || ''

  const query = useInfiniteQuery({
    queryKey: ['admin-assets-logos', prefix],
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      api.getLogoAssets({
        prefix: prefix || undefined,
        cursor: pageParam,
      }) as Promise<LogoAssetsPage>,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: LogoAssetsPage) => lastPage.cursor ?? undefined,
  })

  const items = query.data?.pages.flatMap(p => p.items) ?? []

  const handleCopy = useCallback(
    async (item: LogoAsset) => {
      const text = item.publicUrl ?? item.url
      try {
        await navigator.clipboard.writeText(text)
        setCopiedKey(item.objectKey)
        window.setTimeout(() => setCopiedKey(null), 2000)
        toast({
          title: 'Copied',
          description: item.publicUrl
            ? 'Public URL copied to clipboard'
            : 'Logo URL copied to clipboard',
        })
      } catch {
        toast({
          title: 'Copy failed',
          description: 'Could not copy to clipboard',
          variant: 'destructive',
        })
      }
    },
    [toast]
  )

  if (query.isLoading) {
    return (
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {Array.from({ length: 10 }).map((_, i) => (
          <LoadingSkeleton key={i} className="aspect-4/3 rounded-lg" />
        ))}
      </div>
    )
  }

  if (query.error) {
    return <ErrorState error={query.error} />
  }

  if (items.length === 0) {
    return (
      <EmptyState message="No image files found in storage yet, or none under the configured prefix." />
    )
  }

  return (
    <div className="space-y-6">
      <LogoPreviewDialog
        item={previewItem}
        open={previewItem !== null}
        onOpenChange={nextOpen => {
          if (!nextOpen) {
            setPreviewItem(null)
          }
        }}
      />
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
        {items.map(item => (
          <LogoCard
            key={item.objectKey}
            item={item}
            copied={copiedKey === item.objectKey}
            onCopy={handleCopy}
            onOpenPreview={setPreviewItem}
          />
        ))}
      </div>
      {query.hasNextPage ? (
        <div className="flex justify-center">
          <Button
            variant="outline"
            onClick={() => query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
          >
            {query.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
